import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    TextField,
    IconButton,
    Tooltip,
    Autocomplete,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Iconify } from 'src/components';

/**
 * Toolbar control that lets a script editor pick a treatment to simulate.
 * Shared between the JSON-edit Preview (`ImportScriptModal`) and the listing-row
 * Preview dialog (`pages/.../callScriptV2/CallScriptV2.jsx`) — same purple accent,
 * grouped-by-category Autocomplete, and clear (X) action in both surfaces.
 *
 * Stateless: the parent owns `simulatedTreatment` state.
 */
const SimulateTreatmentPicker = ({
    simulatedTreatment,
    onChange,
    onClear,
    options,
    disabled = false,
}) => {
    const theme = useTheme();
    const isEmpty = options.length === 0;
    const isActive = !!simulatedTreatment;

    return (
        <Box
            data-testid="simulate-treatment-toolbar"
            sx={{
                px: '12px',
                py: '8px',
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
            }}
        >
            <Iconify
                icon="mdi:source-branch"
                width={16}
                height={16}
                sx={{
                    color: isActive ? '#7635DC' : theme.palette.text.secondary,
                    flexShrink: 0,
                }}
            />
            <Typography
                sx={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    flexShrink: 0,
                }}
            >
                Simulate treatment
            </Typography>
            <Tooltip
                arrow
                placement="top"
                title={isEmpty ? 'No treatments referenced in this script.' : ''}
                disableHoverListener={!isEmpty}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Autocomplete
                        size="small"
                        value={simulatedTreatment}
                        onChange={(_, newValue) => onChange(newValue)}
                        options={options}
                        groupBy={(option) => option.category}
                        getOptionLabel={(option) => option?.name || ''}
                        isOptionEqualToValue={(option, value) =>
                            option?.name?.toLowerCase() === value?.name?.toLowerCase()
                        }
                        disabled={disabled || isEmpty}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={
                                    isEmpty
                                        ? 'No treatments referenced'
                                        : 'All questions (no simulation)'
                                }
                                sx={{
                                    '& .MuiInputBase-root': {
                                        minHeight: '28px',
                                        py: '0',
                                        fontSize: '0.8rem',
                                        backgroundColor: isActive
                                            ? alpha('#7635DC', 0.06)
                                            : 'transparent',
                                    },
                                    '& .MuiInputBase-input': { fontSize: '0.8rem' },
                                    '& .MuiOutlinedInput-notchedOutline': isActive
                                        ? { borderColor: alpha('#7635DC', 0.5) }
                                        : {},
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <Box
                                component="li"
                                {...props}
                                key={`${option.category}-${option.name}`}
                                sx={{ fontSize: '0.8rem' }}
                            >
                                <Typography variant="body2">{option.name}</Typography>
                            </Box>
                        )}
                        slotProps={{ popper: { placement: 'bottom-start' } }}
                        sx={{ minWidth: 0 }}
                    />
                </Box>
            </Tooltip>
            {isActive && (
                <Tooltip arrow placement="top" title="Clear simulation">
                    <IconButton
                        size="small"
                        onClick={onClear}
                        aria-label="Clear simulation"
                        sx={{
                            width: 24,
                            height: 24,
                            color: '#7635DC',
                            '&:hover': { bgcolor: alpha('#7635DC', 0.12) },
                        }}
                    >
                        <Iconify icon="mdi:close" width={14} height={14} />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

SimulateTreatmentPicker.propTypes = {
    simulatedTreatment: PropTypes.shape({
        name: PropTypes.string.isRequired,
        category: PropTypes.string,
    }),
    onChange: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            category: PropTypes.string,
        })
    ).isRequired,
    disabled: PropTypes.bool,
};

SimulateTreatmentPicker.defaultProps = {
    simulatedTreatment: null,
};

export default SimulateTreatmentPicker;
